import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import Grid from '@material-ui/core/Grid';
import { lookupsJson, branchInitiatedCancelReasons, flatCancelReasons } from '@ourbranch/lookups';
import { observer } from 'mobx-react';
import flowRight from 'lodash-es/flowRight';
import { isBefore, parseISO, format, subDays } from 'date-fns';
import { getTodayStr } from '@ourbranch/date-fns-helpers';

import { appConfig } from 'brand-config';
import { CognitoPermissionGroups } from 'core/helpers/cognito-permission-groups';
import { FormField } from 'core/components/form';
import withDatePicker from 'core/components/with-date-picker';
import { awsDateToJs } from 'core/helpers/formatters';
import useSession from 'core/hooks/use-session';
import { FieldWithTooltip } from 'core/components/field-with-tooltip';
import { useStore } from 'core/store';
import { tooltipHoverTexts } from 'core/helpers/constants';
import { getEarliestDateAgentAllowedToBackdateCancellation } from 'customer/components/policy/details/helpers/backdate-cancellation';

import { getReasonOptions } from '../cancel-nonrenew-reasons';
import { showRefundCreditCardFeeCheckbox } from './refund-credit-card-fee';
import { cancelMinDate, earliestCancelEffectiveDate } from './cancellation-notice';
import useStyles from './cancellation-form.styles';

const EndDateOfTodayReasons = ['CNDB', 'CNAP', 'CNDC', 'CNDE', 'CNMI', 'CNMV', 'CNNO', 'CNDA', 'CNPR', 'CNSO']; // currently, all EndDateOfTodayReasons are Insured requested
const PermissionGatedReasonsDetails = [
  { code: 'CNMS', permission: 'canCancelReasonCNMS' },
  { code: 'CNIP', permission: 'canCancelReasonCNIP' },
  { code: 'CNPY_X', permission: 'canCancelReasonCNPY_X' },
  { code: 'CNRW', permission: 'canRewritePolicy' },
  { code: 'CNCN', permission: 'canViewCarrierInitiatedCancels' },
  { code: 'CNDP', permission: 'canViewCarrierInitiatedCancels' },
  { code: 'CNPP', permission: 'canViewCarrierInitiatedCancels' },
  { code: 'CNPY', permission: 'canViewCarrierInitiatedCancels' },
  { code: 'NACP', permission: 'canCancelReasonNACP' },
  { code: 'CNUW', permission: 'canViewUWCancelCodes' },
  { code: 'CNUV', permission: 'canViewUWCancelCodes' },
  { code: 'CNUH', permission: 'canViewUWCancelCodes' },
  { code: 'CNSR', permission: 'canViewUWCancelCodes' },
  { code: 'CNFP', permission: 'canViewUWCancelCodes' },
  { code: 'CNSP', permission: 'canViewUWCancelCodes' }
];
// NACP is currently only for AZ Auto policies
const stateGatedReasons = {
  AZ: {
    A: ['NACP']
  }
};
const PermissionGatedReasons = PermissionGatedReasonsDetails.map((detail) => detail.code);

const byValueDescending = (a, b) => {
  if (a.value < b.value) {
    return 1;
  }
  if (a.value > b.value) {
    return -1;
  }
  return 0;
};

const CancellationForm = () => {
  const classes = useStyles();
  const [disableEndDate, setDisableEndDate] = useState(false);
  const { values, setFieldValue, errors } = useFormikContext();
  const {
    endDate,
    effectiveDate,
    state,
    cancelReason,
    transactionDate,
    fullTermPolicyEndDate,
    term,
    cancelEffectiveDate,
    policyType,
    fees
  } = values;

  const session = useSession();
  const {
    account: {
      policies: { policy }
    }
  } = useStore();
  const currentTotalPaid = policy?.totalReceived;
  const longErrorMessage = errors?.cancelEffectiveDate?.length > 60;

  const today = getTodayStr(state);
  const branchInitiated = branchInitiatedCancelReasons.includes(cancelReason);

  // #33591: the visible cancel reasons come from the brand's enabled list when it declares one
  // (mutuals — Forreston/CFM/Oakwood), otherwise the legacy Branch/BIX set. State + permission
  // gating below is layered on regardless of brand.
  const enabledReasonCodes = appConfig?.cancelNonRenewalReasons;
  const gatedCancelReasons = getReasonOptions({
    allReasons: lookupsJson.cancelReason,
    enabledReasonCodes,
    kind: 'cancel'
  }).filter((currentCancelReasonOption) => {
    // filter out codes that are disabled or user doesnt have permission
    const cancelCodeAllowedInThisState = !stateGatedReasons[state]?.[policyType]?.includes(
      currentCancelReasonOption.id
    );
    let hasPermissionToCancelWithThisCode = true;

    if (PermissionGatedReasons.includes(currentCancelReasonOption.id)) {
      const { permission } = PermissionGatedReasonsDetails.find(
        (detail) => detail.code === currentCancelReasonOption.id
      );
      if (currentTotalPaid > 0 && currentCancelReasonOption.id === 'NACP') {
        hasPermissionToCancelWithThisCode = false;
      }
      if (permission && !session[permission]) {
        hasPermissionToCancelWithThisCode = false;
      }
    }

    return cancelCodeAllowedInThisState && hasPermissionToCancelWithThisCode;
  });

  // A brand enabled-list defines its own display order (doc order); the legacy set is alphabetized.
  const cancelReasons = enabledReasonCodes?.length
    ? gatedCancelReasons
    : [...gatedCancelReasons].sort(byValueDescending);

  const isTodayBeforeEffectiveDate = isBefore(parseISO(today), parseISO(effectiveDate));
  const originalEndDate = awsDateToJs(values.fullTermPolicyEndDate);
  // for non branchInitiated, we use cancelEffectiveDate, so we need be able to select up to the full term end date (which will translate to endDate === cancelEffectiveDate -1)
  const maxDate = branchInitiated
    ? new Date(new Date(originalEndDate).setDate(originalEndDate.getUTCDate() - 1))
    : new Date(new Date(originalEndDate).setDate(originalEndDate.getUTCDate() + 1));

  const backdateMinDate = getEarliestDateAgentAllowedToBackdateCancellation(effectiveDate, session.canBackDate);
  const latestCancelEffectiveDate = format(maxDate, 'yyyy-MM-dd');
  const noticeMinDate = cancelMinDate({
    brandAppConfig: appConfig,
    cancelReason,
    today,
    latestDate: latestCancelEffectiveDate,
    canCancelNonPayException: session.canCancelReasonCNPY_X
  });
  const minDateForCancellation = noticeMinDate ?? backdateMinDate;

  useEffect(() => {
    // endDate is always cancelEffectiveDate - 1, unless flat cancel
    if (cancelEffectiveDate && effectiveDate === cancelEffectiveDate) {
      setFieldValue('endDate', effectiveDate);
    } else if (cancelEffectiveDate) {
      setFieldValue('endDate', format(subDays(parseISO(cancelEffectiveDate), 1), 'yyyy-MM-dd'));
    }
  }, [cancelEffectiveDate, effectiveDate, setFieldValue]);

  useEffect(() => {
    // First term ODEN policies always get fee. ODEN policies use transactionDate instead of endDate for cancels.
    if (term === 1 && endDate !== effectiveDate && transactionDate !== effectiveDate) {
      setFieldValue('applyFee', true);
    } else if (endDate === effectiveDate) {
      setFieldValue('applyFee', false);
      setFieldValue('fullTermCancel', false);
    }
    if (endDate === policy.policy.endDate && !transactionDate) {
      setFieldValue('fullTermCancel', true);
    } else {
      setFieldValue('fullTermCancel', false);
    }
  }, [endDate, effectiveDate, term, transactionDate]);

  const updateSuggestedEndDate = (reason) => {
    const branchInitiated = branchInitiatedCancelReasons.includes(reason);

    if (flatCancelReasons.includes(cancelReason)) {
      setFieldValue('cancelEffectiveDate', effectiveDate);
      setDisableEndDate(true);
    } else if (EndDateOfTodayReasons.includes(cancelReason)) {
      setFieldValue('cancelEffectiveDate', isTodayBeforeEffectiveDate ? effectiveDate : today);
      setDisableEndDate(false);
    } else if (branchInitiated) {
      // branchInitiated uses transactionDate, so reset end date to full term, reset cancel effective date to undefined
      setFieldValue('endDate', fullTermPolicyEndDate);
      setFieldValue('cancelEffectiveDate', undefined);
      setFieldValue('transactionDate', isTodayBeforeEffectiveDate ? effectiveDate : today);
    } else {
      // everything else, default to today or effective date; on a mutual, to its notice floor
      const noticeDefault = earliestCancelEffectiveDate({
        brandAppConfig: appConfig,
        cancelReason: reason,
        today,
        latestDate: latestCancelEffectiveDate
      });
      setFieldValue('cancelEffectiveDate', noticeDefault ?? (isTodayBeforeEffectiveDate ? effectiveDate : today));
      setDisableEndDate(false);
    }
    if (!branchInitiated) {
      // if not branch initiated, should not have transactionDate
      setFieldValue('transactionDate', undefined);
    }
  };

  return (
    <Grid container key="details" className={classes.form}>
      <Grid className={classes.cancelFieldsContainer}>
        <FormField
          name="cancelReason"
          type="select"
          label="Cancel Reason"
          mode="light"
          options={cancelReasons}
          xs={cancelReason ? 5 : 8}
          className={cancelReason && cancelReason !== 'NA' ? classes.cancelReasonSmall : classes.cancelReasonBig}
          ignoreGlobalDisabledState // cancel policy sets all form fields to disabled, so need to manually exclude this field
          onChange={(val) => {
            updateSuggestedEndDate(val);
          }}
          permissions={{ isLicensedAction: false }}
        />
        {cancelReason && cancelReason !== 'NA' && (
          <Grid className={classes.endDateContainer}>
            <FieldWithTooltip
              // no longer directly setting endDate for cancels. For transactionDate, user selects date and tehn ODEN calculates endDate. For cancelEffectiveDate, endDate = cancelEffectiveDate -1
              name={branchInitiated ? 'transactionDate' : 'cancelEffectiveDate'}
              label={branchInitiated ? 'Transaction Date' : 'Cancel Effective Date'}
              type="date"
              mode="light"
              disableFuture={false}
              maxDate={maxDate}
              minDate={minDateForCancellation}
              initialFocusedDate={maxDate}
              xs={4}
              disabled={disableEndDate}
              labelNextToTooltip
              className={longErrorMessage ? classes.endDateWithError : classes.endDate}
              ignoreGlobalDisabledState // cancel policy sets all form fields to disabled, so need to manually exclude this field
              defaultToEmpty
              tooltip={branchInitiated ? tooltipHoverTexts.transactionDate : tooltipHoverTexts.cancelEffectiveDate}
              permissions={{ isLicensedAction: false }}
            />
          </Grid>
        )}
      </Grid>
      <FormField
        type="checkbox"
        id="applyFee"
        name="applyFee"
        mode="light"
        label="Apply Fee"
        xs={2}
        className={classes.applyFee}
        ignoreGlobalDisabledState // cancel policy sets all form fields to disabled, so need to manually exclude this field
        permissions={{
          edit: {
            groups: [CognitoPermissionGroups.canEditFeeCheckbox]
          }
        }}
      />
      {showRefundCreditCardFeeCheckbox({ fees, policy: policy?.policy }) && (
        // #36221: card-paid mutual policies keep the credit card processing fee already collected on
        // the payments made by default; staff can refund it instead. Initial state: brand config.
        // Hidden on original imported terms, whose fee line the backend leaves whole.
        <FormField
          type="checkbox"
          id="refundCreditCardFee"
          name="refundCreditCardFee"
          mode="light"
          label="Refund credit card fee"
          xs={3}
          className={classes.applyFee}
          ignoreGlobalDisabledState // cancel policy sets all form fields to disabled, so need to manually exclude this field
          permissions={{ isLicensedAction: false }}
        />
      )}
    </Grid>
  );
};

export default flowRight(withDatePicker, observer)(CancellationForm);
